import { useEffect, useMemo, useRef, useState } from 'react';
import { Markdown } from '../ui';

/** Divide o markdown em blocos por linha em branco, sem quebrar dentro de cercas ```. */
function splitBlocks(md: string): string[] {
  const lines = md.split('\n');
  const blocks: string[] = [];
  let cur: string[] = [];
  let inFence = false;
  const flush = () => { if (cur.join('\n').trim()) blocks.push(cur.join('\n')); cur = []; };
  for (const line of lines) {
    if (/^\s*```/.test(line)) inFence = !inFence;
    if (!inFence && line.trim() === '') flush();
    else cur.push(line);
  }
  flush();
  return blocks;
}

type Editing = { mode: 'block'; index: number } | { mode: 'doc' } | null;

/**
 * Editor inline estilo Notion: mostra o preview renderizado; 2 cliques num bloco
 * abrem só aquele trecho pra edição; 3 cliques abrem o documento inteiro. Volta pra
 * preview (salvando) após `idleMs` sem digitar OU ao clicar fora (blur). Transição suave.
 */
export function InlineDocEditor({
  value,
  onSave,
  readOnly = false,
  idleMs = 5000,
}: {
  value: string;
  onSave: (v: string) => void;
  readOnly?: boolean;
  idleMs?: number;
}) {
  const blocks = useMemo(() => splitBlocks(value), [value]);
  const [editing, setEditing] = useState<Editing>(null);
  const [draft, setDraft] = useState('');
  const snapRef = useRef<string[]>([]);
  const idleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clickRef = useRef<{ n: number; t: ReturnType<typeof setTimeout> | null }>({ n: 0, t: null });
  const taRef = useRef<HTMLTextAreaElement>(null);
  const editingRef = useRef<Editing>(null);
  const draftRef = useRef('');
  editingRef.current = editing;
  draftRef.current = draft;

  function clearIdle() { if (idleRef.current) clearTimeout(idleRef.current); idleRef.current = null; }
  function armIdle() { clearIdle(); idleRef.current = setTimeout(() => commit(), idleMs); }

  function commit() {
    clearIdle();
    const cur = editingRef.current;
    if (!cur) return;
    let v: string;
    if (cur.mode === 'doc') v = draftRef.current;
    else { const b = [...snapRef.current]; b[cur.index] = draftRef.current; v = b.join('\n\n'); }
    setEditing(null);
    if (v !== value) onSave(v);
  }

  function onClickBlock(i: number) {
    if (readOnly || editing) return;
    const c = clickRef.current;
    c.n += 1;
    if (c.t) clearTimeout(c.t);
    c.t = setTimeout(() => {
      const n = c.n; c.n = 0;
      if (n >= 3) { setDraft(value); setEditing({ mode: 'doc' }); }
      else if (n === 2) { snapRef.current = blocks; setDraft(blocks[i] ?? ''); setEditing({ mode: 'block', index: i }); }
    }, 250);
  }

  // foca o textarea e arma o timer ao entrar em edição.
  useEffect(() => {
    if (editing) { taRef.current?.focus(); armIdle(); }
    return clearIdle;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // auto-resize do textarea conforme o conteúdo.
  useEffect(() => {
    const ta = taRef.current;
    if (ta) { ta.style.height = 'auto'; ta.style.height = `${ta.scrollHeight}px`; }
  }, [draft, editing]);

  const textarea = (extra?: string) => (
    <textarea
      ref={taRef}
      className={`inline-doc-ta ${extra ?? ''}`}
      value={draft}
      onChange={(e) => { setDraft(e.target.value); armIdle(); }}
      onBlur={() => commit()}
      spellCheck={false}
    />
  );

  if (editing?.mode === 'doc') {
    return <div className="inline-doc is-editing">{textarea('is-doc')}</div>;
  }

  return (
    <div className="inline-doc">
      {blocks.length === 0 ? (
        <div className="inline-doc-block" onClick={() => onClickBlock(0)} title="2 cliques pra editar">
          <em style={{ color: 'var(--muted)' }}>documento vazio — 2 cliques pra editar</em>
        </div>
      ) : (
        blocks.map((b, i) =>
          editing?.mode === 'block' && editing.index === i ? (
            <div key={i} className="inline-doc-block is-editing">{textarea('is-block')}</div>
          ) : (
            <div
              key={i}
              className="inline-doc-block"
              onClick={() => onClickBlock(i)}
              title={readOnly ? '' : '2 cliques: editar este trecho · 3 cliques: editar tudo'}
            >
              <Markdown text={b} />
            </div>
          ),
        )
      )}
    </div>
  );
}
